import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, BookOpen, Network } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { useTheme } from '../components/providers/ThemeProvider'
import DreamGraphView from '../components/hierarchy/DreamGraphView'
import LifeMapDiagram from '../components/lifemap/LifeMapDiagram'
import { LifeStorybook } from '../components/diary/LifeStorybook'

type View = 'story' | 'diagram' | 'canvas'

export function LifeMapPage() {
  const navigate = useNavigate()
  const { isDiary } = useTheme()
  // In the diary theme the map opens as a storybook — chapters and
  // handwritten entries — with the diagram one tap away.
  //
  // ThemeProvider resolves isDiary in a mount effect, so on a hard load it
  // is still false during the first render — the default must be applied
  // once isDiary settles, not in the useState initializer, and only until
  // the user picks a view themselves.
  const [view, setViewState] = useState<View>('diagram')
  const [kindFilter, setKindFilter] = useState<string | null>(null)
  const userChose = useRef(false)
  useEffect(() => {
    if (isDiary && !userChose.current) setViewState('story')
  }, [isDiary])
  const setView = (v: View) => {
    userChose.current = true
    setViewState(v)
  }
  const showStory = isDiary && view === 'story'

  // Diagram is the drawn map: one SVG, laid out per dream, savable as a file.
  // Canvas is the older free-form board — kept because dragging a node
  // somewhere that makes sense to you is a different thing from reading a
  // tidy picture, and some people want both.
  const views: Array<{ key: View; label: string }> = [
    ...(isDiary ? [{ key: 'story' as View, label: '📖 Storybook' }] : []),
    { key: 'diagram', label: '🗺️ Diagram' },
    { key: 'canvas', label: '🕸️ Canvas' },
  ]

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6" style={{ minHeight: 'calc(100vh - 120px)' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            {showStory ? (
              <BookOpen className="w-5 h-5" style={{ color: 'var(--ink-sepia)' }} />
            ) : (
              <Network className="w-5 h-5" style={{ color: 'var(--brand-primary)' }} />
            )}
            <h1
              className="text-xl md:text-2xl font-semibold"
              style={showStory
                ? { color: 'var(--ink-sepia)', fontFamily: 'var(--font-handwritten)' }
                : { color: 'var(--text-primary)' }}
            >
              {showStory ? 'The story so far' : 'Your full life map'}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            {views.map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => setView(v.key)}
                aria-pressed={view === v.key}
                className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                  isDiary
                    ? ''
                    : view === v.key
                      ? 'bg-brand text-on-brand font-semibold'
                      : 'text-secondary hover:text-brand'
                }`}
                style={isDiary
                  ? view === v.key
                    ? { background: 'var(--ink-sepia)', color: 'var(--paper-cream)', fontFamily: 'var(--font-diary-ui)' }
                    : { color: 'var(--ink-faded)', fontFamily: 'var(--font-diary-ui)' }
                  : undefined}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* The hub row (E12): Life Map is the one room for everything on the
          map, and these chips filter it IN PLACE — matched nodes keep their
          ancestor chain so the tree stays readable. The rail's sub-links
          remain the way to the full entity pages. */}
      {!showStory && (
        <div className="flex flex-wrap items-center gap-2 -mt-1">
          {[
            { key: null, name: 'All' },
            { key: 'dream', name: 'Dreams' },
            { key: 'goal', name: 'Goals' },
            { key: 'habit', name: 'Habits' },
            { key: 'skill', name: 'Skills' },
            { key: 'task', name: 'Tasks' },
            { key: 'world', name: 'World' },
          ].map((c) => (
            <button
              key={c.name}
              type="button"
              aria-pressed={kindFilter === c.key}
              onClick={() => setKindFilter(c.key)}
              className={`px-3.5 py-2 rounded-full text-xs transition-colors ${
                kindFilter === c.key
                  ? 'bg-brand text-on-brand font-semibold'
                  : 'bg-surface border border-soft text-secondary hover:border-brand hover:text-brand'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      {showStory ? (
        <LifeStorybook />
      ) : (
        /* A definite-height FLEX COLUMN: the map card grows inside it via
           flex (percentage heights resolved to 0 here — see DreamGraphView's
           fillHeight note, 2026-08-25). */
        <div
          className="flex flex-col"
          style={{ height: 'calc(100vh - 260px)', minHeight: '420px' }}
        >
          {view === 'canvas' ? (
            <DreamGraphView mode="full" fillHeight kindFilter={kindFilter} />
          ) : (
            <LifeMapDiagram mode="full" fillHeight kindFilter={kindFilter} />
          )}
        </div>
      )}
    </div>
  )
}

export default LifeMapPage
